import React from 'react';
import { notification } from 'antd';
import { NOTIFICATIONDELAY } from '../constants';
import {
  HOT_TITLES, TOP_DEBUT_TITLES, KU_TOP_100, RESURFACED_EXCLUDED, POPULAR, RISING_STARS, TRENDING, AUTHORS,
  PODIUMAUTHORS, TOP_POD_TITLES, MANUALLY_ADDED, COMPETITORS
} from '../../../core/constants';
import { QueryClient } from 'react-query';

const NO_UNDO_IDS = [
  HOT_TITLES, TOP_DEBUT_TITLES, KU_TOP_100, RESURFACED_EXCLUDED, POPULAR, RISING_STARS, TRENDING, AUTHORS,
  PODIUMAUTHORS, TOP_POD_TITLES, MANUALLY_ADDED, COMPETITORS
];

let notifCounter = 0;

export interface IActionNotificationDestination {
  name: string;
  onGoTo: () => void;
}

export interface IShowActionNotificationOptions {
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  destination?: IActionNotificationDestination;
  onUndo?: () => void | Promise<void>;
  duration?: number;
  id?: string;
  refetchKeys?: string[];
  queryClient?: QueryClient;
}

const NotificationActions: React.FC<{
  destination?: IActionNotificationDestination;
  onUndo?: () => void | Promise<void>;
  notifKey: string;
  id?: string;
  refetchKeys?: string[];
  queryClient?: QueryClient;
}> = ({ destination, onUndo, notifKey, id, refetchKeys, queryClient }) => {
  const handleGoTo = () => {
    notification.close(notifKey);
    destination?.onGoTo();
  };

  const handleUndo = async () => {
    notification.close(notifKey);

    try {
      await onUndo?.();

      if (refetchKeys?.length && queryClient) {
        await Promise.all(refetchKeys.map((key) => queryClient.refetchQueries(key)));
      }
    } catch {
      notification.error({
        message: 'Undo failed. Please try again.',
        duration: NOTIFICATIONDELAY,
        className: 'brenda-notification brenda-notification-error'
      });
    }
  };

  return (
    <div className='brenda-notif-actions'>
      {destination && (
        <button className='brenda-notif-btn' onClick={handleGoTo}>
          Go to {destination.name}
        </button>
      )}
      {destination && onUndo && !(id && NO_UNDO_IDS.includes(id)) && (
        <span className='brenda-notif-sep'>·</span>
      )}
      {onUndo && !(id && NO_UNDO_IDS.includes(id)) && (
        <button className='brenda-notif-btn' onClick={handleUndo}>
          Undo
        </button>
      )}
    </div>
  );
};

const showActionNotification = (options: IShowActionNotificationOptions): string => {
  const { type, message, destination, onUndo, duration = 6,
    id, refetchKeys = id ? [`get-row-count${id}`, `get-${id}`] : [], queryClient } = options;
  notifCounter += 1;
  const key = `brenda-notif-${notifCounter}`;

  notification[type]({
    key,
    message,
    duration,
    className: `brenda-notification brenda-notification-${type}`,
    ...(destination || onUndo
      ? {
        btn: (
          <NotificationActions
            destination={destination}
            onUndo={onUndo}
            notifKey={key}
            id={id}
            refetchKeys={refetchKeys}
            queryClient={queryClient}
          />
        )
      }
      : {})
  });

  return key;
};

export default showActionNotification;
